// src/domains/core/context-intelligence/services/backgroundService.ts

import { evaluationService } from "./evaluationService";
import { InsightGenerationService } from "./insightGenerationService";

/**
 * Background Service
 * Periodically executes rules and generates insights
 *
 * Note: This service should be initialized in a React component
 * that has access to the indicator data hooks
 */
export class BackgroundService {
  private intervalId: NodeJS.Timeout | null = null;
  private isRunning = false;

  /**
   * Start background processing
   *
   * @param getIndicatorValue - Function to get current indicator values
   *
   * @param intervalMs - Interval in milliseconds (default: 5 minutes)
   *
   * @returns void
   *
   */
  start(
    getIndicatorValue: (indicatorId: string) => null | number,
    intervalMs: number = 5 * 60 * 1000,
  ): void {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;

    // Run immediately
    void this.process(getIndicatorValue);

    // Then run periodically
    this.intervalId = setInterval(() => {
      void this.process(getIndicatorValue);
    }, intervalMs);
  }

  /**
   * Stop background processing
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
  }

  /**
   * Process rules and generate insights
   *
   * @param getIndicatorValue
   *
   */
  private async process(getIndicatorValue: (indicatorId: string) => null | number): Promise<void> {
    try {
      // Generate insights from rules
      await InsightGenerationService.processRules(getIndicatorValue);

      // Archive outdated insights
      await evaluationService.archiveOutdatedInsights();
    } catch {
      // NOTE: Background insight generation errors are intentionally swallowed to keep service running
      // A future logging integration should capture this error for diagnostics.
    }
  }

  /**
   * Check if service is running
   *
   * @returns true if service is running, false otherwise
   *
   */
  get running(): boolean {
    return this.isRunning;
  }
}

/**
 * Singleton Background Service Instance
 */
export const backgroundService = new BackgroundService();
